import { HeidiUpgradeBaseModal } from '@/components/modal/heidi-upgrade-base-modal/heidi-upgrade-base-modal';
import { PriceDisplay } from '@/components/modal/heidi-upgrade-base-modal/price-display';
import { isAppleCompliantBuild } from '@/utils/appConfiguration';
import { EvidencePowerUserRightPanel } from '@heidi/commerce';
import { Period } from '@heidi/commerce/contracts';
import { TierProFilled } from '@heidi/design-system/icons';
import { TypographyH2, TypographyP2 } from '@heidi/design-system/typography';
import { Check, Loader2 } from 'lucide-react';
import { useIntl } from 'react-intl';
import {
  useEvidencePowerUserUpgradeModal,
  type UseEvidencePowerUserUpgradeModalProps,
} from './useEvidencePowerUserUpgradeModal';

export type EvidencePowerUserUpgradeModalProps = UseEvidencePowerUserUpgradeModalProps;

/**
 * Contextual upgrade modal shown to power users of Evidence during their Clinician trial.
 *
 * Mirrors the functionality of `ClinicianUpgradeModal` (same target tier, checkout flow,
 * price display, segmented-control tabs) but with evidence-focused copy and a personalised
 * usage count on the right-hand panel.
 *
 * Design: https://www.figma.com/design/MSqFiRMYvicVZlaiWjmFG3/Trials---upgrades?node-id=9562-47495
 */
export const EvidencePowerUserUpgradeModal = ({
  launchOpen,
  onLaunchClose,
  countOverride,
}: EvidencePowerUserUpgradeModalProps = {}) => {
  const intl = useIntl();
  const {
    isOpen,
    handleOpenChange,
    handleUpgradeClick,
    isPending,
    clinicianProduct,
    evidenceCount,
    isEvidenceCountLoading,
    isUpgradeDisabled,
    isUpgradeDisabledForTeamMember,
  } = useEvidencePowerUserUpgradeModal({ launchOpen, onLaunchClose, countOverride });

  if (isAppleCompliantBuild()) return null;

  const upgradeDisabledTooltip = isUpgradeDisabledForTeamMember
    ? intl.formatMessage({
        defaultMessage: 'Contact your team admin to upgrade your plan.',
        id: 'iaae8Vpf8H',
        description:
          'Tooltip shown on the disabled Upgrade button in the Evidence power-user upgrade modal for non-admin team members',
      })
    : intl.formatMessage({
        defaultMessage: 'Contact sales to upgrade your team plan.',
        id: 'fwomnoUpor',
        description:
          'Tooltip shown on the disabled Upgrade button in the Evidence power-user upgrade modal for sales-managed teams',
      });

  const features = [
    {
      id: 'unlimited-evidence',
      icon: Check,
      description: intl.formatMessage({
        defaultMessage: 'Unlimited Evidence in Scribe',
        id: 'OSMuaI7RAU',
        description:
          'Feature bullet in the Evidence power-user upgrade modal: unlimited Evidence queries within a Scribe session',
      }),
    },
    {
      id: 'curated-guidelines',
      icon: Check,
      description: intl.formatMessage({
        defaultMessage: 'Curated clinical guideline sources',
        id: '1UcKiChVaU',
        description:
          'Feature bullet in the Evidence power-user upgrade modal: access to curated clinical guidelines',
      }),
    },
    {
      id: 'library-and-cpd',
      icon: Check,
      description: intl.formatMessage({
        defaultMessage: 'Evidence library and CPD activity log',
        id: 'Ei0yof9ZX2',
        description:
          'Feature bullet in the Evidence power-user upgrade modal: personal Evidence library and CPD activity log',
      }),
    },
  ];

  return (
    <HeidiUpgradeBaseModal
      hideTestimonial={false}
      showPeriodTabs
      isUpgradeDisabled={isUpgradeDisabled}
      upgradeDisabledTooltip={upgradeDisabledTooltip}
      title={
        <div className="flex flex-col gap-2">
          <TypographyH2 className="text-balance text-text-primary">
            {intl.formatMessage({
              defaultMessage: 'Evidence is saving you time in session',
              id: 'VIFuGdZiPx',
              description: 'Headline of the Evidence power-user upgrade modal',
            })}
          </TypographyH2>
          <TypographyP2 className="text-pretty text-text-secondary">
            {intl.formatMessage({
              defaultMessage: 'Upgrade to keep Evidence in your session workflow',
              id: 'DirYOi8HO8',
              description: 'Subtitle of the Evidence power-user upgrade modal',
            })}
          </TypographyP2>
        </div>
      }
      features={features}
      yearlyContent={
        clinicianProduct ? (
          <PriceDisplay period={Period.ONE_YEAR} product={clinicianProduct} />
        ) : null
      }
      monthlyContent={
        clinicianProduct ? (
          <PriceDisplay period={Period.ONE_MONTH} product={clinicianProduct} />
        ) : null
      }
      rightPanelContent={
        <EvidencePowerUserRightPanel
          count={evidenceCount}
          isLoading={isEvidenceCountLoading}
        />
      }
      primaryUpgradeButtonContent={
        <>
          <TierProFilled size={16} aria-hidden />
          {isPending ? (
            <Loader2 className="animate-spin" aria-hidden />
          ) : (
            intl.formatMessage({
              defaultMessage: 'Upgrade to the Clinician plan',
              id: '+hDO0d3KkF',
              description:
                'Primary CTA on the Evidence power-user upgrade modal to upgrade to Clinician Pro',
            })
          )}
        </>
      }
      primaryCtaReplacement={isAppleCompliantBuild() ? () => null : undefined}
      isUpgradePending={isPending}
      onUpgradeClick={handleUpgradeClick}
      open={isOpen}
      onOpenChange={handleOpenChange}
    />
  );
};
